<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useCurrentOrg, useOrgUsers, useUpdateOrg } from "@/queries/orgs";
import { useSessionStore } from "@/stores/session-store";
import type { IUser } from "@/types";

const { org, orgLoading } = useCurrentOrg();
const { orgUsers, orgUsersLoading } = useOrgUsers();
const { updateOrgAsync, updateOrgPending } = useUpdateOrg();
const sessionStore = useSessionStore();
const { session } = storeToRefs(sessionStore);

const selectedUserId = ref<string>("");
watch(
  org,
  (o) => { if (o) selectedUserId.value = o.primaryContactUserId ?? ""; },
  { immediate: true }
);

const canEdit = computed(() =>
  session.value.isSuperAdmin || session.value.role === "OrgAdmin"
);

// Pending invitees are excluded — a primary contact must be an activated org member.
const eligibleUsers = computed(() =>
  orgUsers.value.filter((u) => !u.isPending)
);

const hasChanges = computed(() =>
  !!org.value && selectedUserId.value !== (org.value.primaryContactUserId ?? "")
);

const canSave = computed(() =>
  canEdit.value &&
  hasChanges.value &&
  selectedUserId.value !== "" &&
  !updateOrgPending.value
);

const loading = computed(() => orgLoading.value || orgUsersLoading.value);

const successMessage = ref("");
const errorMessage = ref("");

function userLabel(u: IUser): string {
  const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  if (name && u.email) return `${name} — ${u.email}`;
  return name || u.email || u.id;
}

const currentContactLabel = computed(() => {
  const id = org.value?.primaryContactUserId;
  if (!id) return "";
  const match = orgUsers.value.find((u) => u.id === id);
  return match ? userLabel(match) : "";
});

async function handleSave() {
  if (!canSave.value) return;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    await updateOrgAsync({ primaryContactUserId: selectedUserId.value });
    successMessage.value = "Primary contact updated successfully";
    setTimeout(() => { successMessage.value = ""; }, 3000);
  } catch (err) {
    errorMessage.value = err instanceof Error ? err.message : "Failed to update primary contact";
  }
}

function handleReset() {
  if (org.value) selectedUserId.value = org.value.primaryContactUserId ?? "";
}
</script>

<template>
  <div v-if="loading" class="text-center py-5">
    <div class="spinner-border text-secondary" role="status">
      <span class="visually-hidden">Loading contact…</span>
    </div>
    <p class="text-muted small mt-3 mb-0">Loading contact…</p>
  </div>

  <form v-else @submit.prevent="handleSave">
    <div v-if="successMessage" class="alert alert-success" role="alert">
      <i class="bi bi-check-circle me-2"></i>{{ successMessage }}
    </div>
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
    </div>

    <p class="text-muted small mb-4">
      The primary contact is the person other people in your organisation can
      reach out to with questions about this org's BlackBox setup.
    </p>

    <div class="row mb-3">
      <label for="primary-contact" class="col-sm-3 col-form-label">Primary contact</label>
      <div class="col-sm-9">
        <template v-if="canEdit">
          <select
            id="primary-contact"
            v-model="selectedUserId"
            class="form-select"
            :disabled="updateOrgPending"
          >
            <option value="" disabled>Select a user…</option>
            <option
              v-for="u in eligibleUsers"
              :key="u.id"
              :value="u.id"
            >
              {{ userLabel(u) }}
            </option>
          </select>
          <div v-if="eligibleUsers.length === 0" class="form-text">
            No active members are available. Invited users must accept their invitation before
            they can be set as the primary contact.
          </div>
        </template>
        <template v-else>
          <div class="form-control-plaintext">
            <span v-if="currentContactLabel">{{ currentContactLabel }}</span>
            <span v-else class="text-muted fst-italic">No primary contact set</span>
          </div>
        </template>
      </div>
    </div>

    <div v-if="canEdit" class="d-flex justify-content-end gap-2">
      <button
        type="button"
        class="bb-btn-cancel"
        :disabled="!hasChanges || updateOrgPending"
        @click="handleReset"
      >
        Cancel
      </button>
      <button type="submit" class="bb-btn-primary-cta" :disabled="!canSave">
        <span v-if="updateOrgPending" class="spinner-border spinner-border-sm me-2"></span>
        {{ updateOrgPending ? "Saving..." : "Save Changes" }}
      </button>
    </div>
  </form>
</template>
